// ============================================================================
// CONFIG SETTING FIELD
// ============================================================================
// One row on the Settings screen — renders a single definition with:
//   • Label (definition.displayName) + optional unit + ScopeChip showing
//     WHERE the currently-visible value came from (inherited from ENTITY,
//     overridden here at BUSINESS_UNIT, etc.)
//   • Data-type-aware editor (String / Integer / Decimal / Boolean / List)
//   • Reset button — visible when there's an override AT the current scope
//     that can be cleared to fall back through the chain.
//
// Read-only when the caller-selected scope exceeds the definition's
// maxScopeLevel (framework refuses overrides deeper than declared).
// ============================================================================

import React, { useCallback, useMemo } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import ScopeChip from 'components/common/ScopeChip';
import ConfigListEditor from './ConfigListEditor';
import type {
  ConfigDefinitionDto,
  ConfigScopeType,
  EffectiveConfigEntry,
} from 'api/types/config.types';
import type { PendingEdit } from 'hooks/api/useConfigSettings';

interface ConfigSettingFieldProps {
  definition: ConfigDefinitionDto;
  /** Undefined when the effective endpoint returned no entry for this key. */
  effective?: EffectiveConfigEntry;
  /** Pending unsaved edit for this key (undefined if unchanged). */
  editedValue?: PendingEdit;
  activeScopeType: ConfigScopeType;
  onValueChange: (configKey: string, value?: string, items?: string[]) => void;
  onResetValue: (configKey: string) => void;
}

// Scope depth per plan D-9 — used to compare vs definition.maxScopeLevel.
const SCOPE_DEPTH: Record<ConfigScopeType, number> = {
  SYSTEM: 0,
  ENTERPRISE: 1,
  ENTITY: 2,
  BUSINESS_UNIT: 3,
  FINANCIAL_YEAR: 4,
  VOUCHER_TYPE: 5,
};

const ConfigSettingField: React.FC<ConfigSettingFieldProps> = ({
  definition,
  effective,
  editedValue,
  activeScopeType,
  onValueChange,
  onResetValue,
}) => {
  // Depth of the currently-selected scope exceeds what this definition allows?
  const isReadOnly = SCOPE_DEPTH[activeScopeType] > definition.maxScopeLevel;

  // Current display value: pending edit > effective resolved > definition default.
  const currentValue = useMemo(() => {
    if (editedValue?.value !== undefined) return editedValue.value;
    return effective?.value ?? definition.defaultValue ?? '';
  }, [editedValue, effective, definition]);

  const currentListItems = useMemo(() => {
    if (editedValue?.items !== undefined) return editedValue.items;
    return effective?.items ?? [];
  }, [editedValue, effective]);

  // Reset only meaningful when there's an override AT the active scope OR a
  // pending edit — reset for framework defaults / inherited values is a no-op.
  const canReset =
    !isReadOnly &&
    (editedValue !== undefined ||
      (effective?.resolvedScopeType === activeScopeType && !effective?.fromDefault));

  const handleChange = useCallback(
    (value: string) => onValueChange(definition.configKey, value),
    [definition.configKey, onValueChange],
  );
  const handleListChange = useCallback(
    (items: string[]) => onValueChange(definition.configKey, undefined, items),
    [definition.configKey, onValueChange],
  );
  const handleReset = useCallback(
    () => onResetValue(definition.configKey),
    [definition.configKey, onResetValue],
  );

  const renderControl = () => {
    switch (definition.dataType) {
      case 'Boolean':
        return (
          <label className="config-toggle-switch">
            <input
              type="checkbox"
              checked={currentValue === 'true' || currentValue === 'True'}
              onChange={(e) => handleChange(e.target.checked ? 'true' : 'false')}
              disabled={isReadOnly}
            />
            <span className="config-toggle-slider" />
          </label>
        );

      case 'Integer':
        return (
          <input
            className="config-number-input"
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseInt(definition.minValue, 10) : undefined}
            max={definition.maxValue ? parseInt(definition.maxValue, 10) : undefined}
            step={1}
            disabled={isReadOnly}
          />
        );

      case 'Decimal':
        return (
          <input
            className="config-number-input"
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseFloat(definition.minValue) : undefined}
            max={definition.maxValue ? parseFloat(definition.maxValue) : undefined}
            step={0.01}
            disabled={isReadOnly}
          />
        );

      case 'List':
        return (
          <ConfigListEditor
            items={currentListItems}
            onChange={handleListChange}
            unitLabel={definition.unitLabel}
            isReadOnly={isReadOnly}
          />
        );

      default: {
        // String — offer a select if allowed_values encodes a JSON enum, else free text.
        const enumOptions = definition.validationRule?.startsWith('enum:')
          ? (definition.validationRule.slice(5).split(',').map((v) => ({
              value: v.trim(),
              label: v.trim(),
            })) as SelectOption[])
          : null;
        if (enumOptions) {
          return (
            <div style={{ minWidth: '220px' }}>
              <SyutSelect
                value={currentValue}
                onChange={(v: string | string[] | null) =>
                  handleChange(typeof v === 'string' ? v : '')
                }
                options={enumOptions}
                isDisabled={isReadOnly}
                size="sm"
              />
            </div>
          );
        }
        return (
          <input
            className="config-text-input"
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            disabled={isReadOnly}
          />
        );
      }
    }
  };

  return (
    <div
      className={`config-setting-row${definition.dataType === 'List' ? ' config-setting-row--list' : ''}`}
      data-field={definition.configKey}
    >
      <div className="config-setting-info">
        <div className="config-setting-label">
          <span>{definition.displayName}</span>
          {definition.unitLabel && (
            <span className="config-setting-unit">({definition.unitLabel})</span>
          )}
          <ScopeChip
            resolvedFromScopeType={effective?.resolvedScopeType}
            resolvedFromScopeName={effective?.resolvedScopeDisplayName}
            currentScopeType={activeScopeType}
            fromDefault={effective?.fromDefault}
            compact
          />
          {isReadOnly && (
            <span className="config-badge config-badge-system-only" title={`Max scope: ${definition.maxScopeLevel}`}>
              Read-only at this scope
            </span>
          )}
        </div>
        {definition.description && (
          <div className="config-setting-description">{definition.description}</div>
        )}
      </div>
      <div className="config-setting-control">
        {renderControl()}
        {canReset && (
          <button
            type="button"
            className="config-reset-btn"
            onClick={handleReset}
            title="Clear the override at this scope (falls back to parent scope value)"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfigSettingField;
